import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { MessageCircle, Send, X, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const LABELS = {
  fr: { title: 'Commentaires', placeholder: 'Écrire un commentaire…', send: 'Envoyer', empty: 'Aucun commentaire — soyez le premier.', loginNeeded: 'Connexion requise', error: 'Erreur', deleteConfirm: 'Supprimer ce commentaire ?', deleted: 'Commentaire supprimé', close: 'Fermer' },
  en: { title: 'Comments', placeholder: 'Write a comment…', send: 'Send', empty: 'No comments yet — be the first.', loginNeeded: 'Please log in', error: 'Error', deleteConfirm: 'Delete this comment?', deleted: 'Deleted', close: 'Close' },
  es: { title: 'Comentarios', placeholder: 'Escribir un comentario…', send: 'Enviar', empty: 'Sin comentarios — sé el primero.', loginNeeded: 'Inicia sesión', error: 'Error', deleteConfirm: '¿Eliminar este comentario?', deleted: 'Eliminado', close: 'Cerrar' },
  de: { title: 'Kommentare', placeholder: 'Kommentar schreiben…', send: 'Senden', empty: 'Noch keine Kommentare.', loginNeeded: 'Anmelden', error: 'Fehler', deleteConfirm: 'Diesen Kommentar löschen?', deleted: 'Gelöscht', close: 'Schließen' },
  it: { title: 'Commenti', placeholder: 'Scrivi un commento…', send: 'Invia', empty: 'Nessun commento.', loginNeeded: 'Accedi', error: 'Errore', deleteConfirm: 'Eliminare questo commento?', deleted: 'Eliminato', close: 'Chiudi' },
  pt: { title: 'Comentários', placeholder: 'Escrever um comentário…', send: 'Enviar', empty: 'Sem comentários.', loginNeeded: 'Faça login', error: 'Erro', deleteConfirm: 'Apagar este comentário?', deleted: 'Apagado', close: 'Fechar' },
  nl: { title: 'Reacties', placeholder: 'Schrijf een reactie…', send: 'Verstuur', empty: 'Nog geen reacties.', loginNeeded: 'Log in', error: 'Fout', deleteConfirm: 'Deze reactie verwijderen?', deleted: 'Verwijderd', close: 'Sluiten' },
  ar: { title: 'التعليقات', placeholder: 'اكتب تعليقًا…', send: 'إرسال', empty: 'لا توجد تعليقات — كن الأول.', loginNeeded: 'يرجى تسجيل الدخول', error: 'خطأ', deleteConfirm: 'حذف هذا التعليق؟', deleted: 'تم الحذف', close: 'إغلاق' },
  ru: { title: 'Комментарии', placeholder: 'Написать комментарий…', send: 'Отправить', empty: 'Нет комментариев.', loginNeeded: 'Войдите', error: 'Ошибка', deleteConfirm: 'Удалить этот комментарий?', deleted: 'Удалено', close: 'Закрыть' },
  zh: { title: '评论', placeholder: '写评论…', send: '发送', empty: '暂无评论 — 抢占沙发。', loginNeeded: '请登录', error: '错误', deleteConfirm: '删除此评论？', deleted: '已删除', close: '关闭' },
  ja: { title: 'コメント', placeholder: 'コメントを書く…', send: '送信', empty: 'コメントはまだありません。', loginNeeded: 'ログインしてください', error: 'エラー', deleteConfirm: 'このコメントを削除しますか？', deleted: '削除しました', close: '閉じる' },
  hi: { title: 'टिप्पणियाँ', placeholder: 'टिप्पणी लिखें…', send: 'भेजें', empty: 'कोई टिप्पणी नहीं।', loginNeeded: 'लॉगिन करें', error: 'त्रुटि', deleteConfirm: 'यह टिप्पणी हटाएँ?', deleted: 'हटा दी गई', close: 'बंद' },
  sw: { title: 'Maoni', placeholder: 'Andika maoni…', send: 'Tuma', empty: 'Hakuna maoni bado.', loginNeeded: 'Ingia', error: 'Hitilafu', deleteConfirm: 'Futa maoni haya?', deleted: 'Yamefutwa', close: 'Funga' },
};

/**
 * CommentsModal — Bottom-sheet style modal to comment on any content.
 * Props: contentType, contentId, onClose, lang
 */
export default function CommentsModal({ contentType, contentId, onClose, lang = 'fr' }) {
  const { user } = useAuth();
  const L = { ...LABELS.fr, ...(LABELS[(lang || 'fr').toLowerCase()] || {}) };
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`/api/reactions/comments/${contentType}/${contentId}`);
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [contentType, contentId]);

  const send = async () => {
    if (!text.trim() || sending) return;
    if (!user) { toast.error(L.loginNeeded); return; }
    setSending(true);
    try {
      await axios.post('/api/reactions/comment', { content_type: contentType, content_id: contentId, text: text.trim() });
      setText('');
      await load();
    } catch (e) {
      if (e?.response?.status === 401) toast.error(L.loginNeeded); else toast.error(L.error);
    } finally { setSending(false); }
  };

  const del = async (id) => {
    if (!window.confirm(L.deleteConfirm)) return;
    try {
      await axios.delete(`/api/reactions/comment/${id}`);
      toast.success(L.deleted);
      await load();
    } catch { toast.error(L.error); }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-md flex items-end sm:items-center justify-center" onClick={onClose} data-testid="comments-modal">
      <div onClick={e => e.stopPropagation()} className="w-full sm:max-w-lg max-h-[85vh] bg-gray-900 border border-white/10 rounded-t-3xl sm:rounded-3xl flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h3 className="font-bold flex items-center gap-2"><MessageCircle className="w-4 h-4 text-fuchsia-300" /> {L.title} <span className="text-xs text-white/50">({total})</span></h3>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10" data-testid="comments-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="comments-list">
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin mx-auto text-fuchsia-300" />
          ) : items.length === 0 ? (
            <div className="text-center text-white/50 text-sm py-8">{L.empty}</div>
          ) : items.map(c => (
            <div key={c.id} className="p-3 rounded-2xl bg-white/5 border border-white/10">
              <div className="flex items-center gap-2 mb-1">
                {c.avatar_url ? (
                  <img src={c.avatar_url} alt="" className="w-6 h-6 rounded-full object-cover" />
                ) : (
                  <div className="w-6 h-6 rounded-full bg-fuchsia-500/30 text-fuchsia-200 text-[10px] flex items-center justify-center">{(c.user_name || '?').slice(0, 1).toUpperCase()}</div>
                )}
                <span className="text-xs font-semibold">{c.user_name}</span>
                <span className="text-[10px] text-white/40 ml-auto">{new Date(c.created_at).toLocaleDateString(lang)}</span>
                {user && c.user_id === user.id && (
                  <button onClick={() => del(c.id)} className="p-1 rounded-full hover:bg-red-500/20" data-testid={`comment-delete-${c.id}`}>
                    <Trash2 className="w-3 h-3 text-red-300" />
                  </button>
                )}
              </div>
              <p className="text-sm text-white/90 whitespace-pre-wrap break-words">{c.text}</p>
            </div>
          ))}
        </div>
        <div className="p-3 border-t border-white/10 flex items-center gap-2">
          <textarea value={text} onChange={e => setText(e.target.value)} placeholder={L.placeholder} rows={1} className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm text-white placeholder-white/40 focus:outline-none focus:border-fuchsia-400 resize-none" data-testid="comment-input" />
          <button onClick={send} disabled={sending || !text.trim()} className="p-2 rounded-full bg-fuchsia-500 hover:bg-fuchsia-600 disabled:opacity-40" data-testid="comment-send">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}
